import { useEffect, useState } from "react";
import { stockApi, businessesApi } from "@/lib/api";
import { useAuth, isMaster, userBusinessIds } from "@/lib/auth";
import { toast } from "sonner";
import { LineChart as LineChartIcon, Plus } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SUGGESTED = ["coffee beans", "milk", "bacon", "sugar", "flour", "butter"];

export default function StockPrices() {
  const { user } = useAuth();
  const [businesses, setBusinesses] = useState([]);
  const [bizId, setBizId] = useState("");
  const [latest, setLatest] = useState([]);
  const [selected, setSelected] = useState("");
  const [history, setHistory] = useState([]);
  const [form, setForm] = useState({ ingredient: "", price: 0, unit: "kg", vendor: "" });

  useEffect(() => {
    businessesApi.list().then((b) => {
      setBusinesses(b);
      setBizId(isMaster(user) ? (b[0]?.id || "") : userBusinessIds(user)[0] || "");
    });
  }, [user]);

  useEffect(() => {
    if (bizId) stockApi.latest(bizId).then(setLatest);
  }, [bizId]);

  useEffect(() => {
    if (selected) stockApi.history(selected, bizId).then((h) => setHistory([...h].reverse()));
  }, [selected, bizId]);

  const record = async (e) => {
    e.preventDefault();
    try {
      await stockApi.record({ ...form, price: parseFloat(form.price) || 0, business_id: bizId });
      toast.success("Price recorded");
      setForm({ ingredient: "", price: 0, unit: "kg", vendor: "" });
      stockApi.latest(bizId).then(setLatest);
      if (selected) stockApi.history(selected, bizId).then((h) => setHistory([...h].reverse()));
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-4xl font-bold text-espresso" data-testid="stock-title">Stock prices</h1>
          <p className="text-espresso-ink/60 mt-2">Ingredient price history exposed via <code>/api/v1/stock-prices/*</code> for your Stock Control system.</p>
        </div>
        {isMaster(user) && (
          <select data-testid="stock-biz" value={bizId} onChange={(e) => setBizId(e.target.value)} className="px-3 py-2 bg-white border border-canvas-border rounded-lg text-sm brand-focus">
            {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      <div className="bg-espresso/5 border border-espresso/10 rounded-md p-4 text-sm text-espresso-ink">
        <p><b>What is this?</b> Stock Prices tracks ingredient costs over time (coffee beans, milk, bacon, etc.) and exposes them via the public API <code className="bg-white px-1">/api/v1/stock-prices/*</code> — so your Recipe / Stock Control app can pull the latest supplier prices and flag margin changes. Record a price whenever an invoice comes in.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={record} className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft space-y-4">
          <h2 className="font-display text-lg font-bold text-espresso">Record price point</h2>
          <div>
            <input data-testid="stock-ingredient" required list="ingredients" placeholder="Ingredient (e.g. coffee beans)" value={form.ingredient} onChange={(e) => setForm({ ...form, ingredient: e.target.value })} className="w-full px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
            <datalist id="ingredients">{SUGGESTED.map((s) => <option key={s} value={s} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="stock-price" required type="number" step="0.01" placeholder="Price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
            <input data-testid="stock-unit" placeholder="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="w-full px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
          </div>
          <input data-testid="stock-vendor" placeholder="Vendor (optional)" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} className="w-full px-3 py-2.5 border border-canvas-border rounded-lg brand-focus" />
          <button data-testid="stock-submit" className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-espresso text-canvas rounded-lg font-semibold hover:bg-espresso-light">
            <Plus className="w-4 h-4" /> Record
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft">
            <h2 className="font-display text-lg font-bold text-espresso mb-4">Latest prices</h2>
            {latest.length === 0 ? (
              <div className="py-8 text-center text-espresso-ink/50">No prices yet.</div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-xs uppercase tracking-wider text-espresso-ink/60 border-b border-canvas-border">
                    <th className="text-left py-2 font-semibold">Ingredient</th>
                    <th className="text-left py-2 font-semibold">Price</th>
                    <th className="text-left py-2 font-semibold">Vendor</th>
                    <th className="text-left py-2 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {latest.map((r) => (
                    <tr key={r.ingredient} onClick={() => setSelected(r.ingredient)} className="cursor-pointer border-b border-canvas-border hover:bg-canvas" data-testid={`stock-row-${r.ingredient}`}>
                      <td className="py-3 font-medium capitalize text-espresso">{r.ingredient}</td>
                      <td className="py-3 text-espresso font-semibold">R {r.price?.toFixed(2)} / {r.unit}</td>
                      <td className="py-3 text-sm text-espresso-ink">{r.vendor || "—"}</td>
                      <td className="py-3 text-xs text-espresso-ink/60">{new Date(r.recorded_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selected && (
            <div className="bg-white border border-canvas-border rounded-2xl p-6 shadow-soft">
              <div className="flex items-center gap-2 mb-4">
                <LineChartIcon className="w-4 h-4 text-gold" />
                <h3 className="font-display text-lg font-bold text-espresso capitalize">{selected} — history</h3>
              </div>
              {history.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={history.map((h) => ({ ...h, when: new Date(h.recorded_at).toLocaleDateString() }))}>
                    <CartesianGrid stroke="#EFECE6" strokeDasharray="3 3" />
                    <XAxis dataKey="when" stroke="#8a7a6a" fontSize={11} />
                    <YAxis stroke="#8a7a6a" fontSize={11} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EFECE6", borderRadius: 8 }} />
                    <Line type="monotone" dataKey="price" stroke="#C68A4C" strokeWidth={2} dot={{ fill: "#1F1610", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-8 text-espresso-ink/50">No history</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
